import { Fragment, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import AnalyticsHeader from '../components/AnalyticsHeader';
import { MetricLabel } from '../components/MetricHelp';
import AnalyticsChange, { changeDirection } from '../components/AnalyticsChange';
import AnalyticsDateControl from '../components/AnalyticsDateControl';
import '../components/AnalyticsWorkspace.css';
import {
  AnalyticsFilterActions,
  AnalyticsFilterBar,
  AnalyticsFilterGroup,
  AnalyticsPeriodGroup,
  AnalyticsPeriodPresets,
  AnalyticsRefreshButton,
  AnalyticsSecondaryButton,
  AnalyticsSelectField,
} from '../components/AnalyticsFilterBar';
import Pagination from '../components/Pagination';
import { apiRequest } from '../utils/auth';
import { formatMoment, formatNumber, formatPeriod, periodPreset, hktToday, recentDays, validateRange } from '../utils/periodDashboard';
import type { CreditSource, CreditUsageData, CreditUsageSignal, CreditUsageUser } from '../types/creditUsage';
import './CreditUsagePage.css';

const FUNCTIONS: Record<string, string> = {
  transform: 'Transform',
  blend: 'Blend',
  extension: 'Use lens',
  createExtension: 'Create lens',
  upscale: 'Upscale',
};
const SOURCES: { key: CreditSource; label: string }[] = [
  { key: 'free', label: 'Free' },
  { key: 'monthly', label: 'Monthly' },
  { key: 'rollover', label: 'Rollover' },
  { key: 'topup', label: 'Top-up' },
  { key: 'unknown', label: 'Unknown' },
];
const SIGNALS: { key: CreditUsageSignal; label: string; action: string }[] = [
  {
    key: 'inactive',
    label: 'Stopped using',
    action: 'Review their last experience before a reactivation follow-up.',
  },
  {
    key: 'declining',
    label: 'Usage dropped',
    action: 'Check which functions lost usage and ask what changed.',
  },
  {
    key: 'low_balance',
    label: 'Low balance',
    action: 'Check their current plan and upcoming renewal before suggesting a top-up.',
  },
  {
    key: 'high_refunds',
    label: 'Frequent credit releases',
    action: 'Review released operations for failures or cancellations.',
  },
  {
    key: 'unresolved',
    label: 'Credit issues',
    action: 'Inspect the affected operations and reconcile the credit status.',
  },
];
const options = (values: Record<string, string>) =>
  Object.entries(values).map(([value, label]) => ({ value, label }));
const ratio = (numerator: number, denominator: number) =>
  denominator ? `${((numerator / denominator) * 100).toFixed(1)}%` : '—';
const average = (total: number, count: number) =>
  count ? formatNumber(Number((total / count).toFixed(1))) : '—';
function change(current: number, previous: number): string {
  if (!previous) return current ? 'From zero' : 'No change';
  const percent = ((current - previous) / previous) * 100;
  return `${percent > 0 ? '+' : ''}${percent.toFixed(1)}%`;
}

function UserDetails({
  user,
  data,
  operationType,
}: {
  user: CreditUsageUser;
  data: CreditUsageData;
  operationType: string;
}) {
  const query = new URLSearchParams({
    startDate: data.period.startDate,
    endDate: data.period.endDate,
    userId: user.userId,
  });
  if (operationType !== 'all') query.set('operationType', operationType);
  const previousQuery = new URLSearchParams(query);
  previousQuery.set('startDate', data.previousPeriod.startDate);
  previousQuery.set('endDate', data.previousPeriod.endDate);
  const visibleFunctions = user.functions.filter((item) => item.operations > 0 || item.previousCredits > 0);
  return (
    <section className="cu-details" aria-label={`Credit details for ${user.name || user.userId}`}>
      <div className="cu-section-heading">
        <h3>Where these credits went</h3>
        <Link to={`/generation-usage?${query}`}>View operations & attempts →</Link>
      </div>
      <p className="cu-muted">
        Period details: {data.period.startDate} – {data.period.endDate} · HKT ·{' '}
        {operationType === 'all' ? 'All functions' : FUNCTIONS[operationType] || operationType}.
      </p>
      <div className="cu-detail-grid">
        <div>
          {visibleFunctions.length ? (
            <table className="cu-detail-table">
              <caption>Period usage, with prior-period credits for comparison</caption>
              <thead>
                <tr>
                  <th scope="col">Function</th>
                  <th scope="col">Credits consumed</th>
                  <th scope="col">Prior credits</th>
                  <th scope="col">Confirmed / all operations</th>
                  <th scope="col">Released credits</th>
                </tr>
              </thead>
              <tbody>
                {visibleFunctions.map((item) => (
                  <tr key={item.operationType}>
                    <td>{FUNCTIONS[item.operationType] || item.operationType}</td>
                    <td>{formatNumber(item.credits)}</td>
                    <td>{formatNumber(item.previousCredits)}</td>
                    <td>
                      {formatNumber(item.confirmedOperations)} / {formatNumber(item.operations)}
                    </td>
                    <td>{formatNumber(item.releasedCredits)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p>No operations in this period.</p>
          )}
          <p className="cu-muted">
            Credit source:{' '}
            {SOURCES.map(({ key, label }) => `${label} ${formatNumber(user[key])}`).join(' · ')}
          </p>
          {user.previousCredits > 0 ? (
            <Link to={`/generation-usage?${previousQuery}`}>View prior-period operations →</Link>
          ) : null}
        </div>
        <div>
          <dl className="cu-detail-metrics">
            <div>
              <dt>Period average credits / confirmed operation</dt>
              <dd>{average(user.credits, user.confirmedOperations)}</dd>
            </div>
            <div>
              <dt>Period reserved / released credits</dt>
              <dd>
                {formatNumber(user.reservedCredits)} / {formatNumber(user.releasedCredits)}
              </dd>
            </div>
            <div>
              <dt>Period expired / failed credits</dt>
              <dd>
                {formatNumber(user.expiredCredits)} / {formatNumber(user.failedCredits)}
              </dd>
            </div>
            <div>
              <dt>All-time confirmed credits · all functions</dt>
              <dd>{formatNumber(user.lifetimeCredits)}</dd>
            </div>
            <div>
              <dt>Latest confirmed use · all time · all functions · HKT</dt>
              <dd>{user.lifetimeLastUsedAt ? formatMoment(user.lifetimeLastUsedAt) : 'No recorded use'}</dd>
            </div>
          </dl>
          <p className="cu-muted">All-time credits count all retained confirmed reservations.</p>
          {user.signals.length ? (
            <ul className="cu-advice">
              {user.signals.map((signal) => {
                const definition = SIGNALS.find((item) => item.key === signal)!;
                return (
                  <li key={signal}>
                    <strong>{definition.label}:</strong> {definition.action}
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="cu-muted">No follow-up rule triggered for this period.</p>
          )}
        </div>
      </div>
    </section>
  );
}

export default function CreditUsagePage({ embedded = false }: { embedded?: boolean }) {
  const [params, setParams] = useSearchParams();
  const requestParams = new URLSearchParams(params);
  if (!requestParams.has('sort')) requestParams.set('sort', 'lastUsed');
  const query = requestParams.toString();
  const appliedStart = params.get('startDate') || recentDays(30).startDate;
  const appliedEnd = params.get('endDate') || hktToday();
  const appliedRange = { startDate: appliedStart, endDate: appliedEnd };
  const appliedSearch = params.get('search') || '';
  const operationType = params.get('operationType') || 'all';
  const signal = params.get('signal') || 'all';
  const [start, setStart] = useState(appliedStart);
  const [end, setEnd] = useState(appliedEnd);
  const [search, setSearch] = useState(appliedSearch);
  const [dateError, setDateError] = useState('');
  const [data, setData] = useState<CreditUsageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [expanded, setExpanded] = useState<string | null>(null);
  useEffect(() => {
    setStart(appliedStart);
    setEnd(appliedEnd);
    setDateError('');
  }, [appliedStart, appliedEnd]);
  useEffect(() => {
    setSearch(appliedSearch);
  }, [appliedSearch]);
  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setLoading(true);
    setError('');
    setData(null);
    setExpanded(null);
    apiRequest<CreditUsageData>(`/api/admin/credit-usage?${query}`, { signal: controller.signal })
      .then((result) => {
        if (active) setData(result);
      })
      .catch((err: unknown) => {
        if (active) setError(err instanceof Error ? err.message : 'Failed to load credit usage.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [query, refresh]);
  const update = (updates: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    next.delete('page');
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === '') next.delete(key);
      else next.set(key, value);
    });
    setParams(next);
  };
  const reset = () => {
    setParams(embedded ? { view: 'segments' } : {});
    setSearch('');
    setDateError('');
    const period = recentDays(30);
    setStart(period.startDate);
    setEnd(period.endDate);
    if (!query) setRefresh((value) => value + 1);
  };
  return (
    <div className={`credit-usage${embedded ? '' : ' page analytics-workspace'}`}>
      {embedded ? <div className="behavior-intro"><h2>User activity &amp; credits</h2></div> : <AnalyticsHeader
        title="Credit usage"
        action={<Link to={`/analytics/behavior?${new URLSearchParams({ ...Object.fromEntries(params), view: 'segments' })}`}>User behavior →</Link>}
      />}
      <AnalyticsFilterBar>
        <AnalyticsPeriodGroup presets={<AnalyticsPeriodPresets
            active={(days) => periodPreset(appliedRange) === days}
            onSelect={(days) => {
              const period = recentDays(days);
              setStart(period.startDate);
              setEnd(period.endDate);
              setDateError('');
              update({ ...period });
            }}
        />}>
          <AnalyticsDateControl
            appliedStart={appliedStart}
            appliedEnd={appliedEnd}
            startDate={start}
            endDate={end}
            onStartDateChange={setStart}
            onEndDateChange={setEnd}
            startMax={hktToday()}
            endMax={hktToday()}
            error={dateError}
            onApply={(event) => {
              event.preventDefault();
              const result = validateRange({ startDate: start, endDate: end });
              if (!result.valid) {
                setDateError(result.error || 'Choose valid dates.');
                return;
              }
              setDateError('');
              update({ startDate: start, endDate: end });
            }}
          />
        </AnalyticsPeriodGroup>
        <AnalyticsFilterGroup>
          <AnalyticsSelectField
            label="Function"
            value={operationType}
            options={options({ all: 'All functions', ...FUNCTIONS })}
            onChange={(value) => update({ operationType: value })}
          />
          <AnalyticsSelectField
            label="Accounts"
            value={params.get('accounts') || 'external'}
            options={options({
              external: 'External accounts',
              internal: 'Internal accounts',
              all: 'All accounts',
            })}
            onChange={(value) => update({ accounts: value })}
          />
        </AnalyticsFilterGroup>
        <AnalyticsFilterActions>
          <AnalyticsRefreshButton onClick={() => setRefresh((value) => value + 1)} />
          <AnalyticsSecondaryButton onClick={reset}>Reset</AnalyticsSecondaryButton>
        </AnalyticsFilterActions>
      </AnalyticsFilterBar>
      <p className="cu-scope">Period: {formatPeriod(appliedRange)}</p>
      <details className="cu-definitions">
        <summary>How to read this report</summary>
        <p>
          Consumption counts confirmed credit reservations only, grouped by operation start time in Hong Kong.
          Reserved, released, expired and failed credits are separate. Totals and function shares cover all
          filtered users, across every page. Existing accounts with no usage are included; deleted accounts
          and operations without a reservation are outside this report.
        </p>
        <p>
          Period and previous-period credits follow the selected function. All-time totals and current
          available balance cover every function. All-time means all retained reservations, not a historical
          balance. Balance is current, includes unexpired completed credit grants, and already excludes held
          credits. Credit sources describe consumed credits, not cash revenue. Inconsistent source records
          appear as Unknown.
        </p>
        <p>
          Follow-up rules are review prompts, not proof of churn or failed generation. They use equal-length
          periods and current credit status; a period including today is incomplete. Low balance compares
          current balance with 3 times the selected period’s average confirmed operation cost. Frequent
          credit releases count released operations, including cancellations; expired operations are separate.
        </p>
      </details>
      {!loading && !error && data ? (
        <>
          <p className="cu-scope">
            Compared with {data.previousPeriod.startDate} – {data.previousPeriod.endDate} · HKT. Current
            balance as of {formatMoment(data.asOf)}.{appliedEnd === hktToday() ? ' Today is incomplete.' : ''}
          </p>
          <div className="cu-summary">
            <article>
              <span><MetricLabel label="Credits consumed" help="Credits charged for confirmed operations in this period. Blue means more usage than before; amber means less. Credits are not revenue, profit or provider cost." /></span>
              <strong>{formatNumber(data.summary.credits)}</strong>
              <small>
                <AnalyticsChange direction={changeDirection(data.summary.credits, data.summary.previousCredits)} preference="neutral" label={change(data.summary.credits, data.summary.previousCredits)} /> · previously{' '}
                {formatNumber(data.summary.previousCredits)}
              </small>
            </article>
            <article>
              <span><MetricLabel label="Users consuming credits" help="Different accounts that used credits in this period, out of all accounts matching the filters. An account can use the app without consuming credits." /></span>
              <strong>
                {formatNumber(data.summary.consumingUsers)} <em>/ {formatNumber(data.summary.users)}</em>
              </strong>
              <small>{ratio(data.summary.consumingUsers, data.summary.users)} of filtered accounts</small>
            </article>
            <article>
              <span><MetricLabel label="Credits / consuming user" help="Total credits consumed divided by the number of accounts that consumed credits. Accounts with no consumption are left out." /></span>
              <strong>{average(data.summary.credits, data.summary.consumingUsers)}</strong>
              <small>
                {average(data.summary.credits, data.summary.confirmedOperations)} per confirmed operation
              </small>
            </article>
            <article>
              <span><MetricLabel label="Released operation rate" help="The share of operations where reserved credits were returned, including cancellations. Expired operations are separate. A credit release is not a payment refund." /></span>
              <strong>{ratio(data.summary.releasedOperations, data.summary.operations)}</strong>
              <small>
                {formatNumber(data.summary.releasedCredits)} credits released ·{' '}
                {formatNumber(data.summary.releasedOperations)} / {formatNumber(data.summary.operations)}{' '}
                operations
              </small>
            </article>
          </div>
          <details className="cu-consumption-details" open={!embedded}><summary>Credit consumption by function &amp; source</summary>
          <div className="cu-breakdowns">
            <section aria-labelledby="cu-functions-heading">
              <div className="cu-section-heading">
                <h2 id="cu-functions-heading">Credits by function</h2>
                <span>Confirmed consumption</span>
              </div>
              {data.summary.credits ? (
                data.functions
                  .filter((item) => item.credits > 0)
                  .map((item) => (
                    <div className="cu-distribution" key={item.operationType}>
                      <div>
                        <button
                          className="cu-text-button"
                          onClick={() => update({ operationType: item.operationType })}
                        >
                          {FUNCTIONS[item.operationType] || item.operationType}
                        </button>
                        <span>
                          {formatNumber(item.credits)}{' '}
                          <small>· {ratio(item.credits, data.summary.credits)}</small>
                        </span>
                      </div>
                      <div className="cu-bar" aria-hidden="true">
                        <span style={{ width: `${(item.credits / data.summary.credits) * 100}%` }} />
                      </div>
                      <small>
                        {formatNumber(item.users)} {item.users === 1 ? 'user' : 'users'} ·{' '}
                        {formatNumber(item.operations)} confirmed operations
                      </small>
                    </div>
                  ))
              ) : (
                <p className="cu-muted">No confirmed consumption in this period.</p>
              )}
            </section>
            <section aria-labelledby="cu-sources-heading">
              <div className="cu-section-heading">
                <h2 id="cu-sources-heading">Credit sources</h2>
                <span>Where consumed credits came from</span>
              </div>
              {SOURCES.map(({ key, label }) => (
                <div className="cu-source" key={key}>
                  <span>{label}</span>
                  <strong>{formatNumber(data.summary[key])}</strong>
                  <small>{ratio(data.summary[key], data.summary.credits)}</small>
                </div>
              ))}
              <p className="cu-muted">
                Free, subscription and top-up credits are usage units. They do not measure revenue or provider
                cost.
              </p>
            </section>
          </div>
          </details>
        </>
      ) : null}
      <section className="cu-users" aria-labelledby="cu-users-heading">
        <div className="cu-section-heading cu-users-heading">
          <h2 id="cu-users-heading">
            {!loading && !error && data
              ? `${formatNumber(data.pagination.total)} ${data.pagination.total === 1 ? 'user' : 'users'}`
              : 'Users'}
          </h2>
          <AnalyticsSelectField
            label="Rows"
            value={params.get('limit') || '25'}
            options={options({ '10': '10', '25': '25', '50': '50', '100': '100' })}
            onChange={(value) => update({ limit: value })}
          />
        </div>
        <div className="cu-list-filters">
          <form
            className="cu-search"
            onSubmit={(event) => {
              event.preventDefault();
              update({ search: search.trim() });
            }}
          >
            <label htmlFor="credit-user-search">Find a user</label>
            <div>
              <input
                id="credit-user-search"
                value={search}
                maxLength={200}
                placeholder="Name, email, handle or user ID"
                onChange={(event) => setSearch(event.target.value)}
              />
              <button type="submit">Search</button>
            </div>
          </form>
          <AnalyticsSelectField
            label="Activity"
            value={params.get('activity') || 'all'}
            options={options({ all: 'All users', used: 'Used credits', unused: 'No credits used' })}
            onChange={(value) => update({ activity: value })}
          />
          <AnalyticsSelectField
            label="Follow-up"
            value={signal}
            options={options({
              all: 'All follow-ups',
              ...Object.fromEntries(SIGNALS.map((item) => [item.key, item.label])),
            })}
            onChange={(value) => update({ signal: value })}
          />
          <AnalyticsSelectField
            label="Sort by"
            value={params.get('sort') || 'lastUsed'}
            options={options({
              credits: 'Most credits used',
              decline: 'Largest credit drop',
              lastUsed: 'Latest period use',
              balance: 'Lowest balance',
            })}
            onChange={(value) => update({ sort: value })}
          />
        </div>
        <p className="cu-scope">
          {appliedStart} – {appliedEnd} · HKT ·{' '}
          {operationType === 'all' ? 'All functions' : FUNCTIONS[operationType] || operationType} ·{' '}
          {params.get('accounts') === 'all'
            ? 'Includes internal accounts'
            : params.get('accounts') === 'internal'
              ? 'Internal accounts only'
              : 'Excludes internal accounts'}
          .
          {appliedSearch ? (
            <>
              {' '}
              Search: “{appliedSearch}”.{' '}
              <button className="cu-text-button" onClick={() => update({ search: null })}>
                Clear search
              </button>
            </>
          ) : null}
          {params.get('userId') ? (
            <>
              {' '}
              User ID: {params.get('userId')}.{' '}
              <button className="cu-text-button" onClick={() => update({ userId: null })}>
                Clear user
              </button>
            </>
          ) : null}
        </p>
        {loading ? (
          <div className="cu-message" role="status">
            Loading credit usage…
          </div>
        ) : error ? (
          <div className="cu-message cu-error" role="alert">
            {error}
            <button onClick={() => setRefresh((value) => value + 1)}>Retry</button>
          </div>
        ) : data ? (
          <>
            {data.rows.length ? (
              <div
                className="cu-table-wrap"
                role="region"
                tabIndex={0}
                aria-label="User credit usage, scroll horizontally for all columns"
              >
                <table className="cu-table">
                  <thead>
                    <tr>
                      <th scope="col">User / current plan</th>
                      <th scope="col"><MetricLabel label="Period credits consumed" help="Credit units charged for confirmed operations in the selected period and function. All-time confirmed credits across all functions are available in Details." /></th>
                      <th scope="col"><MetricLabel label="Change / prior period" help="Credit consumption compared with the previous period of equal length. Blue means more usage; amber means less. From zero means there is no valid percentage baseline." /></th>
                      <th scope="col"><MetricLabel label="Top function" help="The function with the most credits consumed by this account in the selected period." /></th>
                      <th scope="col"><MetricLabel label="Balance now" help="The account’s current credit balance at report time. This is not its balance at the end of the selected period." /></th>
                      <th scope="col">Last period use · HKT</th>
                      <th scope="col"><MetricLabel label="Follow-up" help="Rule-based signals to help you decide which accounts to review. They are prompts for investigation, not confirmed problems." /></th>
                      <th scope="col">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((user, index) => {
                      const top = user.functions.find((item) => item.credits > 0);
                      return (
                        <Fragment key={user.userId}>
                          <tr>
                            <td>
                              <Link to={`/users/${encodeURIComponent(user.userId)}`}>
                                {user.name || user.email || user.userId}
                              </Link>
                              <small>{user.email && user.name ? user.email : user.userId}</small>
                              <small>
                                {user.plan}
                                {user.isInternal ? ' · Internal' : ''}
                              </small>
                            </td>
                            <td>
                              <strong>{formatNumber(user.credits)}</strong>
                              <small>{formatNumber(user.confirmedOperations)} confirmed operations</small>
                            </td>
                            <td>
                              <AnalyticsChange direction={changeDirection(user.credits, user.previousCredits)} preference="neutral" label={change(user.credits, user.previousCredits)} />
                              <small>{formatNumber(user.previousCredits)} previously</small>
                            </td>
                            <td>
                              {top ? FUNCTIONS[top.operationType] || top.operationType : '—'}
                              {top ? (
                                <small>{ratio(top.credits, user.credits)} of period credits</small>
                              ) : null}
                            </td>
                            <td>{formatNumber(user.balance)}</td>
                            <td>{user.lastUsedAt ? formatMoment(user.lastUsedAt) : 'No use this period'}</td>
                            <td>
                              {user.signals.length ? (
                                user.signals.map((key) => (
                                  <span className={`cu-signal cu-signal-${key}`} key={key}>
                                    {SIGNALS.find((item) => item.key === key)?.label}
                                  </span>
                                ))
                              ) : (
                                <span className="cu-muted">No signal</span>
                              )}
                            </td>
                            <td>
                              <button
                                aria-expanded={expanded === user.userId}
                                aria-controls={`cu-user-${index}`}
                                aria-label={`${expanded === user.userId ? 'Hide' : 'Show'} credit details for ${user.name || user.userId}`}
                                onClick={() => setExpanded(expanded === user.userId ? null : user.userId)}
                              >
                                {expanded === user.userId ? 'Hide' : 'Details'}
                              </button>
                            </td>
                          </tr>
                          {expanded === user.userId ? (
                            <tr>
                              <td colSpan={8} id={`cu-user-${index}`}>
                                <UserDetails user={user} data={data} operationType={operationType} />
                              </td>
                            </tr>
                          ) : null}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="cu-message">
                {data.pagination.total
                  ? 'No users on this page. Choose a page below.'
                  : 'No users match these filters. Try clearing the search or follow-up filter.'}
              </div>
            )}
            <Pagination
              variant="analytics"
              currentPage={data.pagination.page}
              totalPages={data.pagination.pages}
              onPageChange={(page) => {
                const next = new URLSearchParams(params);
                next.set('page', String(page));
                setParams(next);
              }}
            />
          </>
        ) : null}
      </section>
    </div>
  );
}
